import type {
  CompleteWeatherResponse,
  CurrentWeatherResponse,
  HourlyWeatherResponse,
  WeatherHistoryResponse,
  ForecastResponse,
  LocationNameResponse,
  FishingScoreResponse,
} from '@fishing-app/shared';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

export class WeatherAPI {
  /**
   * Get complete weather data (location + current + hourly + daily + fishing score)
   * This is the main endpoint - use this for most cases
   */
  static async getComplete(
    lat: number,
    lng: number,
    species?: string,
    depth?: 'shallow' | 'medium' | 'deep',
    date?: string,
    lang?: string,
    pastDays?: number,
    futureDays?: number
  ): Promise<CompleteWeatherResponse> {
    const params = new URLSearchParams({
      lat: lat.toString(),
      lng: lng.toString(),
    });
    if (species) params.append('species', species);
    if (depth) params.append('depth', depth);
    if (date) params.append('date', date);
    if (lang) params.append('lang', lang);
    if (pastDays !== undefined) params.append('pastDays', pastDays.toString());
    if (futureDays !== undefined) params.append('futureDays', futureDays.toString());

    const response = await fetch(`${BACKEND_URL}/api/weather/complete?${params}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch complete weather: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Get current weather data only
   */
  static async getCurrent(lat: number, lng: number): Promise<CurrentWeatherResponse> {
    const params = new URLSearchParams({
      lat: lat.toString(),
      lng: lng.toString(),
    });

    const response = await fetch(`${BACKEND_URL}/api/weather/current?${params}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch current weather: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Get 24-hour hourly weather forecast
   */
  static async getHourly(lat: number, lng: number): Promise<HourlyWeatherResponse> {
    const params = new URLSearchParams({
      lat: lat.toString(),
      lng: lng.toString(),
    });

    const response = await fetch(`${BACKEND_URL}/api/weather/hourly?${params}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch hourly weather: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Get weather history (past + future daily data)
   */
  static async getHistory(
    lat: number,
    lng: number,
    pastDays: number = 7,
    forecastDays: number = 7
  ): Promise<WeatherHistoryResponse> {
    const params = new URLSearchParams({
      lat: lat.toString(),
      lng: lng.toString(),
      pastDays: pastDays.toString(),
      forecastDays: forecastDays.toString(),
    });

    const response = await fetch(`${BACKEND_URL}/api/weather/history?${params}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch weather history: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Get future weather forecast only
   */
  static async getForecast(
    lat: number,
    lng: number,
    forecastDays: number = 7
  ): Promise<ForecastResponse> {
    const params = new URLSearchParams({
      lat: lat.toString(),
      lng: lng.toString(),
      forecastDays: forecastDays.toString(),
    });

    const response = await fetch(`${BACKEND_URL}/api/weather/forecast?${params}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch weather forecast: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Get location name from coordinates (reverse geocoding)
   */
  static async getLocation(lat: number, lng: number): Promise<LocationNameResponse> {
    const params = new URLSearchParams({
      lat: lat.toString(),
      lng: lng.toString(),
    });

    const response = await fetch(`${BACKEND_URL}/api/weather/location?${params}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch location: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Get detailed fishing score with breakdown
   */
  static async getFishingScore(
    lat: number,
    lng: number,
    species?: string,
    depth?: 'shallow' | 'medium' | 'deep'
  ): Promise<FishingScoreResponse> {
    const params = new URLSearchParams({
      lat: lat.toString(),
      lng: lng.toString(),
    });
    if (species) params.append('species', species);
    if (depth) params.append('depth', depth);

    const response = await fetch(`${BACKEND_URL}/api/fishing/score?${params}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch fishing score: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Get AI recommendation (streaming response)
   * Returns a ReadableStream that can be consumed for real-time updates
   */
  static async getAIRecommendation(
    lat: number,
    lng: number,
    species?: string,
    depth?: 'shallow' | 'medium' | 'deep',
    language: string = 'en'
  ): Promise<ReadableStream<Uint8Array>> {
    const response = await fetch(`${BACKEND_URL}/api/ai/recommendation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        lat,
        lng,
        species,
        depth,
        language,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch AI recommendation: ${response.statusText}`);
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    return response.body;
  }

  /**
   * Helper to consume AI recommendation stream and call callback with chunks
   */
  static async streamAIRecommendation(
    coordinates: { lat: number; lng: number },
    currentWeather: any,
    hourlyWeather: any[],
    dailyWeather: any[],
    fishingScore: any,
    species: string,
    selectedDate: string,
    language: string,
    onChunk: (text: string) => void,
    onComplete: () => void,
    onError: (error: Error) => void,
    fishingContext?: any,
    unitSystem: string = 'metric'
  ): Promise<void> {
    try {
      console.log('🤖 Sending AI request with fishingContext:', fishingContext);
      
      const response = await fetch(`${BACKEND_URL}/api/ai/recommendation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          coordinates,
          currentWeather,
          hourlyWeather,
          dailyWeather,
          fishingScore,
          species,
          selectedDate,
          language,
          languageName: language === 'sv' ? 'Swedish' : language === 'en' ? 'English' : 'Norwegian',
          unitSystem,
          unitNames: unitSystem === 'metric' ? 'METRIC (Celsius, m/s, hPa, mm, km)' : 'IMPERIAL (Fahrenheit, mph, inHg, in, mi)',
          stream: true,
          fishingContext
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ AI recommendation error response:', errorText);
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.error || `Failed to fetch AI recommendation: ${response.statusText}`);
        } catch {
          throw new Error(`Failed to fetch AI recommendation: ${response.statusText} - ${errorText}`);
        }
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let buffer = '';
      
      while (true) {
        const result = await reader.read();
        
        if (!result) {
          console.error('❌ Reader returned null/undefined');
          throw new Error('Stream reader returned null');
        }
        
        const { done, value } = result;
        
        if (done) {
          console.log('✅ Stream complete');
          onComplete();
          break;
        }

        // Decode the chunk
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        // Parse SSE format: "data: {...}\n\n"
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const dataStr = line.slice(6); // Remove "data: " prefix
            try {
              const data = JSON.parse(dataStr);
              
              if (data.error) {
                throw new Error(data.error);
              }
              
              if (data.done) {
                console.log('✅ Received done signal');
                onComplete();
                return;
              }
              
              if (data.token) {
                onChunk(data.token);
              }
            } catch (parseError) {
              console.error('Failed to parse SSE data:', dataStr, parseError);
            }
          }
        }
      }
    } catch (error) {
      console.error('❌ streamAIRecommendation error:', error);
      onError(error as Error);
    }
  }

  /**
   * Chat with AI - follow-up questions
   */
  static async streamAIChat(
    messages: Array<{ role: 'user' | 'assistant'; content: string }>,
    coordinates: { lat: number; lng: number },
    currentWeather: any,
    hourlyWeather: any[],
    dailyWeather: any[],
    species: string,
    selectedDate: string,
    language: string,
    onChunk: (text: string) => void,
    onComplete: () => void,
    onError: (error: Error) => void,
    fishingContext?: any,
    unitSystem: string = 'metric'
  ): Promise<void> {
    try {
      console.log('💬 Sending chat request with', messages.length, 'messages');
      
      const response = await fetch(`${BACKEND_URL}/api/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages,
          coordinates,
          currentWeather,
          hourlyWeather,
          dailyWeather,
          species,
          selectedDate,
          language,
          languageName: language === 'sv' ? 'Swedish' : language === 'en' ? 'English' : 'Norwegian',
          unitSystem,
          unitNames: unitSystem === 'metric' ? 'METRIC (Celsius, m/s, hPa, mm, km)' : 'IMPERIAL (Fahrenheit, mph, inHg, in, mi)',
          fishingContext
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Chat error response:', errorText);
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.error || `Failed to chat: ${response.statusText}`);
        } catch {
          throw new Error(`Failed to chat: ${response.statusText} - ${errorText}`);
        }
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let buffer = '';
      
      while (true) {
        const result = await reader.read();
        
        if (!result) {
          console.error('❌ Reader returned null/undefined');
          throw new Error('Stream reader returned null');
        }
        
        const { done, value } = result;
        
        if (done) {
          console.log('✅ Chat stream complete');
          onComplete();
          break;
        }

        // Decode the chunk
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        // Parse SSE format: "data: {...}\n\n"
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const dataStr = line.slice(6); // Remove "data: " prefix
            try {
              const data = JSON.parse(dataStr);
              
              if (data.error) {
                throw new Error(data.error);
              }
              
              if (data.done) {
                console.log('✅ Received done signal');
                onComplete();
                return;
              }
              
              if (data.token) {
                onChunk(data.token);
              }
            } catch (parseError) {
              console.error('Failed to parse SSE data:', dataStr, parseError);
            }
          }
        }
      }
    } catch (error) {
      console.error('❌ streamAIChat error:', error);
      onError(error as Error);
    }
  }
}
